import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../constants/config';

/**
 * ARCHITECTURE NOTE:
 * This slice manages opportunity DATA (CRUD operations, storage)
 * Works in tandem with opportunityFiltersSlice which manages FILTERS
 * Integration: Filters slice provides search params, this slice fetches/stores data
 */

// Async thunk for fetching all opportunities using zone search endpoint
export const fetchOpportunities = createAsyncThunk(
  'opportunity/fetchOpportunities',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      // Use the new zone search endpoint with POST method
      // Accepts filters directly as JSON body (from opportunityFiltersSlice)
      console.log('Sending POST request with filters:', filters); // Add this before fetch

      const response = await fetch(`${config.BASE_URL}/opportunities/zone/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(filters)
      });

      if (!response.ok) {
        try {
          // Try to parse as JSON first
          const errorData = await response.json();
          return rejectWithValue(errorData);
        } catch (jsonError) {
          // If it's not valid JSON, get the text content
          const textContent = await response.text();

          // Check if it's an HTML response (likely a server error page)
          if (textContent.includes('<!DOCTYPE') || textContent.includes('<html')) {
            return rejectWithValue('Server error: The API returned an HTML page instead of JSON. This might indicate a server-side issue or incorrect API endpoint configuration.');
          }

          // Otherwise return the text content
          return rejectWithValue(`Server error: ${textContent}`);
        }
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Improve error message for common issues
      if (error.message.includes('Unexpected token')) {
        return rejectWithValue('Invalid response format: The server returned data in an unexpected format. This might be due to a server configuration issue.');
      }

      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        return rejectWithValue('Network error: Unable to connect to the server. Please check your internet connection and try again.');
      }

      return rejectWithValue(`Error: ${error.message}`);
    }
  }
);

// Async thunk for fetching a specific opportunity
export const fetchOpportunity = createAsyncThunk(
  'opportunity/fetchOpportunity',
  async (opportunityId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(`${config.OPPORTUNITIES_BASE}/${opportunityId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        try {
          // Try to parse as JSON first
          const errorData = await response.json();
          return rejectWithValue(errorData);
        } catch (jsonError) {
          // If it's not valid JSON, get the text content
          const textContent = await response.text();

          // Check if it's an HTML response (likely a server error page)
          if (textContent.includes('<!DOCTYPE') || textContent.includes('<html')) {
            return rejectWithValue('Server error: The API returned an HTML page instead of JSON. This might indicate a server-side issue or incorrect API endpoint configuration.');
          }

          // Otherwise return the text content
          return rejectWithValue(`Server error: ${textContent}`);
        }
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Improve error message for common issues
      if (error.message.includes('Unexpected token')) {
        return rejectWithValue('Invalid response format: The server returned data in an unexpected format. This might be due to a server configuration issue.');
      }

      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        return rejectWithValue('Network error: Unable to connect to the server. Please check your internet connection and try again.');
      }

      return rejectWithValue(`Error: ${error.message}`);
    }
  }
);

// Async thunk for creating a new opportunity
export const createOpportunity = createAsyncThunk(
  'opportunity/createOpportunity',
  async (opportunityData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(config.CREATE_OPPORTUNITY, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(opportunityData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating an opportunity
export const updateOpportunity = createAsyncThunk(
  'opportunity/updateOpportunity',
  async ({ opportunityId, opportunityData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(`${config.OPPORTUNITIES_BASE}/${opportunityId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(opportunityData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for deleting an opportunity
export const deleteOpportunity = createAsyncThunk(
  'opportunity/deleteOpportunity',
  async (opportunityId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(`${config.OPPORTUNITIES_BASE}/${opportunityId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      return { opportunityId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching book opportunities
export const fetchBookOpportunities = createAsyncThunk(
  'opportunity/fetchBookOpportunities',
  async (bookId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(`${config.GET_BOOK_OPPORTUNITIES.replace(':bookId', bookId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching character opportunities
export const fetchCharacterOpportunities = createAsyncThunk(
  'opportunity/fetchCharacterOpportunities',
  async (characterId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(`${config.GET_CHARACTER_OPPORTUNITIES.replace(':characterId', characterId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching recommended opportunities
export const fetchRecommendedOpportunities = createAsyncThunk(
  'opportunity/fetchRecommendedOpportunities',
  async (actorId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(`${config.GET_RECOMMENDED_OPPORTUNITIES.replace(':actorId', actorId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
// localStorage can throw outright (private mode, site data blocked), and it can hold a sort key
// that no longer exists if the option list changes — so the value is validated, not just read.
const SORT_KEYS = ['tier', 'series', 'alpha', 'lines', 'recent'];
export const readStoredSort = () => {
  try {
    const v = localStorage.getItem('opportunitySortKey');
    return SORT_KEYS.includes(v) ? v : 'tier';
  } catch (_) {
    return 'tier';
  }
};

const initialState = {
  opportunities: [],
  // Server-side paging state. `total` is the count of rows MATCHING THE FILTERS, not the number
  // held in `opportunities` — which is now one page. It is refreshed only when the filters change
  // (the server recomputes it only on a paginated request), so paging back and forth costs one
  // 10-row query and no recount.
  paging: {
    total: 0,
    offset: 0,
    limit: 10,
    // Seeded from the SAME store the marketplace's dropdown reads, so the two cannot disagree.
    //
    // This used to be a hardcoded 'tier' while the dropdown initialised from localStorage. The
    // marketplace's own mount fetch was fine — it sends its component state — but the filter
    // panel sends `paging.sort`, so a filter changed before the first response landed would
    // search in 'tier' order while the dropdown still read "Character name (A-Z)". Two places
    // remembering the same preference is one place too many.
    sort: readStoredSort(),
    // The filter object behind the current result set. The marketplace owns the pager but the
    // filter panel owns the filters, so turning a page has to re-issue the LAST search with a new
    // offset — otherwise page 2 of a filtered result quietly becomes page 2 of everything.
    lastFilters: null
  },
  selectedOpportunity: null,
  bookOpportunities: {},
  characterOpportunities: {},
  recommendedOpportunities: [],
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  filters: {
    status: null,
    bookId: null,
    characterId: null
  }
};

// Create the slice
const opportunitySlice = createSlice({
  name: 'opportunity',
  initialState,
  reducers: {
    clearSelectedOpportunity: (state) => {
      state.selectedOpportunity = null;
    },
    // Flip one variation's clone inclusion in place, without waiting for a round trip.
    //
    // WHY OPTIMISTIC. The write and the re-read are two separate HTTP requests, and the re-read
    // can land on a replica that has not caught the write yet — so the row comes back exactly as
    // it went out and the click appears to have done nothing. The user's response to a button
    // that did nothing is to press it again, which is precisely what was happening: the second
    // press wrote the same value and by then the read was current, so it "took two clicks".
    //
    // Reconciliation still happens — the caller refetches afterwards — but the UI no longer waits
    // for the network to agree before showing what the user just did.
    setVariationExclusion: (state, action) => {
      const { opportunityUuid, variationUuid, excluded } = action.payload || {};
      const opp = state.opportunities.find((o) => o.uuid === opportunityUuid);
      if (!opp) return;
      // Both field names are the same array under two names (see the ALIAS note in
      // opportunityZoneController); updating one and not the other would leave the row
      // disagreeing with itself depending on which the consumer reads.
      ['variationSamples', 'variations'].forEach((key) => {
        (opp[key] || []).forEach((v) => {
          if (v.uuid === variationUuid) v.cloneExcluded = excluded;
        });
      });
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    // Sort is persisted per browser (see OpportunityMarketplace) rather than hardcoded. The
    // shipped default is tier-descending so leads lead, but an actor who always browses
    // alphabetically gets their choice back on the next visit instead of re-picking every time.
    setSort: (state, action) => {
      state.paging.sort = action.payload;
      state.paging.offset = 0;
    },
    clearFilters: (state) => {
      state.filters = {
        status: null,
        bookId: null,
        characterId: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchOpportunities
      .addCase(fetchOpportunities.pending, (state, action) => {
        state.status = 'loading';
        // Remember what was asked for, so the pager can re-ask with only the offset changed.
        state.paging.lastFilters = action.meta.arg || {};
      })
      .addCase(fetchOpportunities.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // The endpoint answers in one of two shapes and both are legitimate: a bare array for
        // callers that never asked to paginate (the Book-Creator pages), and an envelope for
        // callers that sent a `limit`. Reading `payload.opportunities` blindly would set
        // `undefined` for every Book-Creator caller and empty their list.
        if (Array.isArray(action.payload)) {
          state.opportunities = action.payload;
          state.paging.total = action.payload.length;
          state.paging.offset = 0;
        } else {
          state.opportunities = action.payload.opportunities || [];
          state.paging.total = action.payload.total ?? state.opportunities.length;
          state.paging.offset = action.payload.offset ?? 0;
          state.paging.limit = action.payload.limit ?? state.paging.limit;
          state.paging.sort = action.payload.sort ?? state.paging.sort;
        }
        state.error = null;
      })
      .addCase(fetchOpportunities.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch opportunities';
      })

      // Handle fetchOpportunity
      .addCase(fetchOpportunity.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchOpportunity.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.selectedOpportunity = action.payload;
        state.error = null;
      })
      .addCase(fetchOpportunity.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch opportunity';
      })

      // Handle createOpportunity
      .addCase(createOpportunity.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createOpportunity.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.opportunities.push(action.payload);
        state.selectedOpportunity = action.payload;
        state.error = null;
      })
      .addCase(createOpportunity.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to create opportunity';
      })

      // Handle updateOpportunity
      .addCase(updateOpportunity.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateOpportunity.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.opportunities.findIndex(opp => opp.uuid === action.payload.uuid);
        if (index !== -1) {
          // MERGE, do not replace.
          //
          // The list holds an ENRICHED projection — character, book, litGroup, variationSamples,
          // applicationCount — assembled by the zone search. The update endpoint does RETURN o
          // and answers with the bare Opportunity node, nothing else. Assigning it wholesale
          // stripped every joined field off the row that had just been edited: its variations
          // vanished, its character name went blank, and any ordering derived from that data
          // moved it. The symptom was a list that re-sorted itself a few seconds after a status
          // change — the delay being the round trip.
          state.opportunities[index] = { ...state.opportunities[index], ...action.payload };
        }
        // Same reasoning for the detail selection, which several pages read for its joins.
        state.selectedOpportunity = state.selectedOpportunity?.uuid === action.payload.uuid
          ? { ...state.selectedOpportunity, ...action.payload }
          : action.payload;
        state.error = null;
      })
      .addCase(updateOpportunity.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to update opportunity';
      })

      // Handle deleteOpportunity
      .addCase(deleteOpportunity.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteOpportunity.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.opportunities = state.opportunities.filter(
          opp => opp.uuid !== action.payload.opportunityId
        );
        if (state.selectedOpportunity && state.selectedOpportunity.uuid === action.payload.opportunityId) {
          state.selectedOpportunity = null;
        }
        state.error = null;
      })
      .addCase(deleteOpportunity.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to delete opportunity';
      })

      // Handle fetchBookOpportunities
      .addCase(fetchBookOpportunities.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchBookOpportunities.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.bookOpportunities[action.meta.arg] = action.payload;
        state.error = null;
      })
      .addCase(fetchBookOpportunities.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch book opportunities';
      })

      // Handle fetchCharacterOpportunities
      .addCase(fetchCharacterOpportunities.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCharacterOpportunities.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.characterOpportunities[action.meta.arg] = action.payload;
        state.error = null;
      })
      .addCase(fetchCharacterOpportunities.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch character opportunities';
      })

      // Handle fetchRecommendedOpportunities
      .addCase(fetchRecommendedOpportunities.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchRecommendedOpportunities.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.recommendedOpportunities = action.payload;
        state.error = null;
      })
      .addCase(fetchRecommendedOpportunities.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch recommended opportunities';
      });
  }
});

// Export actions
export const {
  clearSelectedOpportunity, setFilters, clearFilters, setSort, setVariationExclusion
} = opportunitySlice.actions;

// Export selectors
export const selectOpportunities = (state) => state.user.opportunity.opportunities;
export const selectSelectedOpportunity = (state) => state.user.opportunity.selectedOpportunity;
export const selectOpportunitiesStatus = (state) => state.user.opportunity.status;
export const selectOpportunitiesError = (state) => state.user.opportunity.error;
export const selectOpportunityFilters = (state) => state.user.opportunity.filters;
export const selectOpportunityPaging = (state) => state.user.opportunity.paging;
// Stable empty-array fallback — a fresh `[]` per call makes useSelector warn
// ("returned a different result … same parameters") and rerender needlessly.
const EMPTY_ARRAY = Object.freeze([]);
export const selectBookOpportunities = (bookId) => (state) => state.user.opportunity.bookOpportunities[bookId] || EMPTY_ARRAY;
export const selectCharacterOpportunities = (characterId) => (state) => state.user.opportunity.characterOpportunities[characterId] || EMPTY_ARRAY;
export const selectRecommendedOpportunities = (state) => state.user.opportunity.recommendedOpportunities;

// Export reducer
export default opportunitySlice.reducer;
